import {
  Component,
  inject,
  OnInit,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { VendorService } from '../vendor.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-vendors',
  standalone: true,
  imports: [FormsModule, RouterLink, CommonModule],
  templateUrl: './vendors.component.html',
  styleUrl: './vendors.component.scss',
})
export class VendorsComponent implements OnInit {
  vendors: any[] = [];
  vendorToDelete: any;
  currentPage = 1;
  pageSize = 10;
  totalProducts = 0;
  activeTab: string = 'vendors';
  uploadUrl = `${environment.apiUrl}/vendors/bulk-upload`;

  pageCount: any;
  vendorsLoading = false;
  vendorInput$ = new Subject<string>();
  private vendorService = inject(VendorService);
  private modalService = inject(NgbModal);
  private toastr = inject(ToastrService);
  searchTerm = '';
  @ViewChild('fileInput') fileInput!: ElementRef;
  uploadType: 'CREATE' | 'UPDATE' | 'MAPPING' = 'CREATE';

  ngOnInit(): void {
    this.getVendors();
    const storedTab = localStorage.getItem('activeVendorTab');
    if (storedTab === 'vendors' || storedTab === 'manufacturers') {
      this.activeTab = storedTab;
    }
    this.vendorInput$
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((term) => {
        this.currentPage = 1;
        this.getVendors();
      });
  }

  getVendors(): void {
    this.vendorsLoading = true;
    const params: any = {
      page: this.currentPage,
      limit: this.pageSize,
    };
    const trimmedSearch = this.searchTerm?.trim();

    if (trimmedSearch) {
      // Use the search API when searchTerm is present
      params.name = trimmedSearch;

      this.vendorService.searchVendors(params).subscribe({
        next: (res: any) => {
          this.vendors = res?.data;
          this.totalProducts = res?.total || 0;
          this.pageCount = res?.totalPages || 1;
          this.vendorsLoading = false;
        },
        error: (error: any) => {
          console.error('Error fetching vendors:', error);
          this.vendorsLoading = false;
        },
      });
    } else {
      // Otherwise use default getVendors API
      this.vendorService.getVendors(params).subscribe({
        next: (res: any) => {
          this.vendors = res?.data || [];
          this.totalProducts = res?.total || 0;
          this.pageCount = res?.totalPages || 1;
          this.vendorsLoading = false;
        },
        error: (error: any) => {
          console.error('Error fetching vendors:', error);
          this.vendorsLoading = false;
        },
      });
    }
  }

  pageSizeChange() {
    this.currentPage = 1;
    this.getVendors();
  }

  onSearch() {
    this.currentPage = 1;
    this.vendorInput$.next(this.searchTerm.trim());
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.pageCount || page === this.currentPage) return;
    this.currentPage = page;
    this.getVendors(); // load the data for this page
  }

  getVisiblePages(): number[] {
    const pages: number[] = [];
    const start = Math.max(2, this.currentPage - 2);
    const end = Math.min(this.pageCount - 1, this.currentPage + 2);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  }

  confirmDelete() {
    if (!this.vendorToDelete?.id) return;

    this.vendorService.deleteVendor(this.vendorToDelete.id).subscribe({
      next: () => {
        this.toastr.success(
          `${this.vendorToDelete.name} deleted successfully.`
        );
        this.getVendors(); // Refresh the vendor list
        this.closeModal(); // Close the modal
      },
      error: (err) => {
        console.error('Error deleting vendor:', err);
        this.toastr.error('Failed to delete the vendor. Please try again.');
      },
    });
  }

  openDeleteConfirmationModal(content: any, vendor: any) {
    this.vendorToDelete = vendor;
    this.modalService.open(content, {
      ariaLabelledBy: 'confirmDeleteModalLabel',
    });
  }

  closeModal() {
    this.modalService.dismissAll();
  }

  setTab(tab: 'vendors' | 'manufacturers') {
    this.activeTab = tab;
    localStorage.setItem('activeVendorTab', tab);
  }

  toggleStatus(vendor: any): void {
    const newStatus = !vendor.isActive; // Invert the current status
    this.vendorsLoading = true;

    // Call your service to update the status on the backend
    this.vendorService.updateVendorStatus(vendor.id, newStatus).subscribe({
      next: (res: any) => {
        // Find the vendor in the local array and update its status
        const index = this.vendors.findIndex((v) => v.id === vendor.id);
        if (index !== -1) {
          this.vendors[index].isActive = newStatus;
          this.toastr.success('Vendor status updated successfully!');
        }
        this.vendorsLoading = false;
      },
      error: (error: any) => {
        console.error('Error updating vendor status:', error);
        this.toastr.error('Failed to update vendor status.');
        this.vendorsLoading = false;
      },
    });
  }

  // --- Bulk Operation Logic ---

  // Download vendor creation template
  downloadCreationTemplate(): void {
    this.vendorService.downloadTemplate().subscribe({
      next: (blob: Blob) => {
        this.triggerDownload(
          blob,
          'vendor-creation-template.xlsx',
          'Template downloaded successfully!'
        );
      },
      error: (error: any) => {
        console.error('Error downloading creation template:', error);
        this.toastr.error('Failed to download creation template.');
      },
    });
  }

  // Download vendor update template
  downloadUpdateTemplate(): void {
    this.vendorService.downloadUpdateTemplate().subscribe({
      next: (blob: Blob) => {
        this.triggerDownload(
          blob,
          'vendor-update-template.xlsx',
          'Template downloaded successfully!'
        );
      },
      error: (error: any) => {
        console.error('Error downloading update template:', error);
        this.toastr.error('Failed to download update template.');
      },
    });
  }

  // Download vendor mapping template
  downloadMappingTemplate(): void {
    this.vendorService.downloadMappingTemplate().subscribe({
      next: (blob: Blob) => {
        this.triggerDownload(
          blob,
          'vendor-mapping-template.xlsx',
          'Template downloaded successfully!'
        );
      },
      error: (error: any) => {
        console.error('Error downloading mapping template:', error);
        this.toastr.error('Failed to download mapping template.');
      },
    });
  }

  // Open export confirmation modal
  openExportModal(content: any) {
    this.modalService.open(content, { centered: true });
  }

  // Export vendors to Excel
  exportToExcel(): void {
    this.vendorService.exportVendors().subscribe({
      next: (blob: Blob) => {
        this.triggerDownload(
          blob,
          'vendors_export.xlsx',
          'Vendors exported successfully!'
        );
        this.closeModal();
      },
      error: (error: any) => {
        console.error('Error exporting vendors:', error);
        this.toastr.error('Failed to export vendors.');
        this.closeModal();
      },
    });
  }

  // Helper to trigger file download
  private triggerDownload(
    blob: Blob,
    fileName: string,
    successMessage: string
  ) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(url);
    this.toastr.success(successMessage);
  }

  // Trigger file input for upload
  triggerUpload(type: 'CREATE' | 'UPDATE' | 'MAPPING'): void {
    this.uploadType = type;
    this.fileInput.nativeElement.value = ''; // Reset input
    this.fileInput.nativeElement.click();
  }

  // Handle file selection
  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      this.toastr.error('Invalid file format. Please upload an Excel file.');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    if (this.uploadType === 'CREATE') {
      this.vendorService.bulkUpload(formData).subscribe({
        next: (res) => this.onUploadSuccess('Bulk creation successful!'),
        error: (err) => this.onUploadError(err),
      });
    } else if (this.uploadType === 'UPDATE') {
      this.vendorService.bulkUpdateUpload(formData).subscribe({
        next: (res) => this.onUploadSuccess('Bulk update successful!'),
        error: (err) => this.onUploadError(err),
      });
    } else {
      this.vendorService.bulkMappingUpload(formData).subscribe({
        next: (res) => this.onUploadSuccess('Bulk mapping upload successful!'),
        error: (err) => this.onUploadError(err),
      });
    }
  }

  // Handle successful bulk upload
  onUploadSuccess(message: string): void {
    this.toastr.success(message);
    this.getVendors(); // Reload vendors list
  }

  // Handle bulk upload error
  onUploadError(event: any): void {
    console.error('Bulk upload error:', event);
    this.toastr.error('Failed to upload vendors.');
  }
}
